import { IBusinessRepository } from '../../domain/interfaces/IBusinessRepository';
import {
  Business,
  BusinessListItemDto,
  BusinessDetailDto,
  BusinessSummary,
  BusinessSearchResponse,
  CreateBusinessCommand,
  UpdateBusinessCommand,
  AdminUpdateBusinessAddressCommand,
  AdminUpdateBusinessAddressResponse
} from '../../domain/entities/Business';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { ApiClient } from '../api/ApiClient';
import { API_ENDPOINTS } from '../api/config';

/**
 * BusinessRepository - Implementation of business repository
 *
 * Handles all API calls related to businesses
 */
export class BusinessRepository implements IBusinessRepository {
  constructor(private readonly apiClient: ApiClient) {}

  /**
   * Get all businesses with pagination, search, filters, and sorting
   * Returns BusinessListItemDto for list display with statistics
   * SESSION_320: Added sortBy and sortDescending parameters
   */
  async getAll(
    params?: PaginationParams,
    searchTerm?: string,
    category?: string,
    regionId?: string,
    subAreaId?: string,
    sortBy?: string,
    sortDescending?: boolean,
    isActive?: boolean
  ): Promise<PaginatedResult<BusinessListItemDto>> {
    return this.apiClient.get<PaginatedResult<BusinessListItemDto>>(API_ENDPOINTS.BUSINESSES.BASE, {
      params: {
        ...params,
        ...(searchTerm && { searchTerm }),
        ...(category && { category }),
        ...(regionId && { regionId }),
        ...(subAreaId && { subAreaId }),
        ...(sortBy && { sortBy }),
        ...(sortDescending !== undefined && { sortDescending }),
        ...(isActive !== undefined && { isActive })
      }
    });
  }

  /**
   * Get distinct business categories for filtering
   */
  async getCategories(): Promise<string[]> {
    return this.apiClient.get<string[]>(`${API_ENDPOINTS.BUSINESSES.BASE}/categories`);
  }

  /**
   * Get a single business by ID with full details
   * Returns BusinessDetailDto for detail pages
   */
  async getById(id: string): Promise<BusinessDetailDto> {
    return this.apiClient.get<BusinessDetailDto>(API_ENDPOINTS.BUSINESSES.BY_ID(id));
  }

  /**
   * Create a new business
   */
  async create(command: CreateBusinessCommand): Promise<Business> {
    return this.apiClient.post<Business>(API_ENDPOINTS.BUSINESSES.BASE, command);
  }

  /**
   * Update an existing business
   */
  async update(id: string, command: UpdateBusinessCommand): Promise<Business> {
    return this.apiClient.put<Business>(API_ENDPOINTS.BUSINESSES.BY_ID(id), command);
  }

  /**
   * Deactivate a business (admin only)
   * Sprint 045: Sets IsActive = false via DELETE endpoint
   */
  async deactivate(id: string): Promise<void> {
    await this.apiClient.delete<void>(API_ENDPOINTS.BUSINESSES.BY_ID(id));
  }

  /**
   * Reactivate a previously deactivated business (admin only)
   * Sprint 045: Sets IsActive = true via POST reactivate endpoint
   */
  async reactivate(id: string): Promise<void> {
    await this.apiClient.post<void>(API_ENDPOINTS.BUSINESSES.REACTIVATE(id));
  }

  /**
   * Update address fields on a business (admin only)
   */
  async updateAdminAddress(
    id: string,
    command: AdminUpdateBusinessAddressCommand
  ): Promise<AdminUpdateBusinessAddressResponse> {
    return this.apiClient.put<AdminUpdateBusinessAddressResponse>(
      API_ENDPOINTS.BUSINESSES.ADMIN_ADDRESS(id),
      command
    );
  }

  /**
   * Search businesses by name (for autocomplete)
   * Critical for registration flow and mention confirmation
   * Session 004: Used for mention confirmation autocomplete
   * SESSION_137: Fixed parameter order - limit before regionId to prevent type mismatch bug
   */
  async search(query: string, limit: number = 10, regionId?: string): Promise<BusinessSummary[]> {
    // Sprint 037: Now returns from paginated endpoint but extracts just results for backwards compatibility
    const response = await this.searchWithPagination(query, 1, limit, regionId);
    return response.results;
  }

  /**
   * Search businesses with pagination and relevance scoring
   * Sprint 037: For registration page with improved sorting and pagination
   * Returns full paginated response with metadata
   */
  async searchWithPagination(
    query: string,
    pageNumber: number = 1,
    pageSize: number = 10,
    regionId?: string
  ): Promise<BusinessSearchResponse> {
    return this.apiClient.get<BusinessSearchResponse>(API_ENDPOINTS.BUSINESSES.SEARCH, {
      params: {
        q: query,
        pageNumber,
        pageSize,
        ...(regionId && { regionId })
      }
    });
  }

  /**
   * Get a public business summary by ID (for registration URL pre-population)
   * SESSION_276: Public endpoint - no authentication required
   * Returns null if business not found
   */
  async getSummaryById(id: string): Promise<BusinessSummary | null> {
    try {
      const response = await this.apiClient.get<{ business: BusinessSummary }>(
        API_ENDPOINTS.BUSINESSES.SUMMARY_BY_ID(id)
      );
      return response.business;
    } catch (error: unknown) {
      // Return null for 404 (business not found)
      if (
        error !== null &&
        typeof error === 'object' &&
        'response' in error &&
        error.response !== null &&
        typeof error.response === 'object' &&
        'status' in error.response &&
        error.response.status === 404
      ) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get all businesses for a company by company ID (for fast registration pre-population)
   * SESSION_276: Avoids slow text search by fetching directly by company ID
   * Public endpoint - no authentication required
   */
  async getByCompanyId(companyId: string): Promise<BusinessSummary[]> {
    const response = await this.apiClient.get<{ businesses: BusinessSummary[] }>(
      API_ENDPOINTS.BUSINESSES.BY_COMPANY_ID(companyId)
    );
    return response.businesses;
  }
}
